import { useState, useRef, useEffect } from "react";
import type { SseCarMessage, CarItem } from "@/api/aiChat";
import { INITIAL_SHOW_COUNT, LAZY_LOAD_STEP } from "./train-card";

// ════════════════════════════════════════════════════
//  租车列表卡片（与 TrainCard 同风格 + 懒加载）
// ════════════════════════════════════════════════════

/**
 * 车型类型样式
 *
 * ★ 后端 car_type 是自由文本（如「硬派越野SUV」「插电混动中型SUV」「轻越野SUV」），
 *   不是固定枚举，因此改为「关键字匹配」而非精确匹配。
 */
type CarTypeStyle = { bg: string; color: string; icon: string };

/** 按关键字顺序匹配（先出现的优先） */
const CAR_TYPE_RULES: { keys: string[]; style: CarTypeStyle }[] = [
  // 越野
  { keys: ["越野", "硬派"], style: { bg: "linear-gradient(135deg, #F59E0B, #D97706)", color: "#fff", icon: "ri-roadster-fill" } },
  // 新能源 / 混动 / 电动
  { keys: ["插电", "混动", "新能源", "纯电", "电动"], style: { bg: "linear-gradient(135deg, #10B981, #059669)", color: "#fff", icon: "ri-leaf-fill" } },
  // MPV / 商务
  { keys: ["MPV", "商务"], style: { bg: "linear-gradient(135deg, #EC4899, #BE185D)", color: "#fff", icon: "ri-bus-2-fill" } },
  // 轿车
  { keys: ["轿车"], style: { bg: "linear-gradient(135deg, #8B5CF6, #6D28D9)", color: "#fff", icon: "ri-taxi-fill" } },
  // SUV（放最后，避免「硬派越野SUV」被 SUV 抢先命中）
  { keys: ["SUV"], style: { bg: "linear-gradient(135deg, #3B82F6, #1D4ED8)", color: "#fff", icon: "ri-car-fill" } },
];

/** 默认样式（未匹配时使用） */
const CAR_TYPE_DEFAULT: CarTypeStyle = { bg: "linear-gradient(135deg, #94A3B8, #64748B)", color: "#fff", icon: "ri-car-fill" };

/** 按 car_type 文本匹配样式 */
function getCarTypeStyle(carType?: string): CarTypeStyle {
  const t = (carType || "").toUpperCase();
  if (!t) return CAR_TYPE_DEFAULT;
  for (const rule of CAR_TYPE_RULES) {
    if (rule.keys.some((k) => t.includes(k.toUpperCase()))) return rule.style;
  }
  return CAR_TYPE_DEFAULT;
}

/**
 * 从车型全称中拆出「车系」与「配置」
 * 例：「宋PLUS DM-i 冠军版 110KM 尊荣型」→ 主名「宋PLUS DM-i」+ 副名「冠军版 110KM 尊荣型」
 */
function splitModel(model: string): { main: string; sub: string } {
  const s = (model || "").trim();
  if (!s) return { main: "", sub: "" };
  const idx = s.indexOf(" ");
  if (idx === -1) return { main: s, sub: "" };
  return { main: s.slice(0, idx), sub: s.slice(idx + 1) };
}

interface CarQueryRaw {
  city: string;
  return_city: string;
  total: number;
  pickup_date: string;
  return_date: string;
  scenario: string;
  ai_text: string;
  cars: CarItem[];
}

export default function CarCard({ data, onCarSelect }: { data: SseCarMessage | CarQueryRaw; onCarSelect?: (car: CarItem) => void }) {
  const cd = ("data" in data && (data as SseCarMessage).data)
    ? (data as SseCarMessage).data
    : data as CarQueryRaw;

  if (!cd?.cars?.length) return null;

  // ★ 懒加载状态（复用常量）
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW_COUNT);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const cars = cd.cars;
  const showCars = cars.slice(0, visibleCount);
  const isAllLoaded = visibleCount >= cars.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isAllLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((prev) => Math.min(prev + LAZY_LOAD_STEP, cars.length));
      },
      { rootMargin: "80px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAllLoaded, cars.length]);

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5 flex-wrap"
        style={{ background: "linear-gradient(135deg, #FFF7ED, #FFEDD5)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-car-line" style={{ color: "#F59E0B", fontSize: 15 }} />
        <span className="text-[12px] font-semibold min-w-0 truncate" style={{ color: "#333" }}>
          {cd.city || "租车"}
          {/* ★ 异地还车城市 */}
          {cd.return_city && cd.return_city !== cd.city ? ` → ${cd.return_city}` : ""}
          {cd.scenario ? ` · ${cd.scenario}` : ""}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono"
          style={{ background: "#FEF3C7", color: "#D97706" }}
        >
          共 {cd.total} 款
        </span>
        {!isAllLoaded && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#D97706" }}>
            已显示 {visibleCount}/{cars.length}
          </span>
        )}
      </div>

      {/* ★ 取还车日期 */}
      {(cd.pickup_date || cd.return_date) && (
        <div className="flex items-center gap-2 px-3.5 py-1.5 flex-wrap text-[9px]"
          style={{ background: "#FFFBF5", borderBottom: "1px solid #F0F2F8", color: "#B45309" }}
        >
          <span className="flex items-center gap-0.5">
            <i className="ri-calendar-check-line" style={{ fontSize: 9 }} />
            取车 {cd.pickup_date || "—"}
          </span>
          <i className="ri-arrow-right-line" style={{ fontSize: 9, color: "#FCD34D" }} />
          <span className="flex items-center gap-0.5">
            <i className="ri-calendar-close-line" style={{ fontSize: 9 }} />
            还车 {cd.return_date || "—"}
          </span>
          {cd.return_city && cd.return_city !== cd.city && (
            <span className="px-1.5 py-0.5 rounded" style={{ background: "#FFEDD5", color: "#C2410A" }}>
              异地还车：{cd.return_city}
            </span>
          )}
        </div>
      )}

      {/* ★ 说明：后端 ai_text 是「品牌 | 车型全称 | 车型组 | 座位数 | 理由」的多行拼接，
          与下方 cars[] 逐条渲染的内容完全重复，故不再整体展示（避免出现文字墙）。 */}

      {/* 车辆列表 */}
      <div className="flex flex-col">
        {showCars.map((car: CarItem, idx: number) => {
          const ct = getCarTypeStyle(car.car_type);
          const { main, sub } = splitModel(car.model);

          return (
            <div
              key={car.brand + idx}
              className={`px-3 py-2.5 flex gap-2.5 ${onCarSelect ? "cursor-pointer active:scale-[0.995]" : "active:bg-[#FFFBF5]"}`}
              style={{
                transition: "background 0.15s, transform 0.1s",
                borderBottom: idx < showCars.length - 1 ? "1px solid #F0F2F8" : "none",
              }}
              onClick={() => onCarSelect?.(car)}
            >
              {/* ★ 左侧：序号徽章 + 车型图标（用类型色区分） */}
              <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-0.5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: ct.bg }}
                >
                  <i className={ct.icon} style={{ color: ct.color, fontSize: 17 }} />
                </div>
                <span className="text-[9px] font-mono leading-none" style={{ color: "#CBD5E1" }}>
                  #{idx + 1}
                </span>
              </div>

              {/* 右侧信息区 */}
              <div className="flex-1 min-w-0">
                {/* 第一行：品牌 + 车系 | 座位数 */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
                    <span className="text-[13px] font-bold flex-shrink-0" style={{ color: "#1E293B" }}>
                      {car.brand}
                    </span>
                    <span className="text-[13px] font-semibold truncate" style={{ color: "#334155" }}>
                      {main}
                    </span>
                  </div>
                  <span className="text-[10px] flex-shrink-0 px-1.5 py-0.5 rounded font-medium"
                    style={{ background: "#FEF3C7", color: "#B45309" }}
                  >
                    <i className="ri-user-line" style={{ fontSize: 9 }} /> {car.seats}座
                  </span>
                </div>

                {/* 第二行：配置描述（灰色小字） */}
                {sub && (
                  <div className="text-[10px] mt-0.5 truncate" style={{ color: "#94A3B8" }} title={car.model}>
                    {sub}
                  </div>
                )}

                {/* 第三行：车型组标签 + 场景 */}
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {car.car_type && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap"
                      style={{ background: "#F1F5F9", color: "#475569" }}
                    >
                      {car.car_type}
                    </span>
                  )}
                  {car.scenario && car.scenario !== cd.scenario && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap"
                      style={{ background: "#FFF7ED", color: "#C2410A" }}
                    >
                      适用：{car.scenario}
                    </span>
                  )}
                </div>

                {/* 第四行：推荐理由（浅色卡片底，与上方视觉分层） */}
                {car.reason && (
                  <div className="mt-1.5 px-2 py-1.5 rounded-md text-[10px] leading-relaxed"
                    style={{ background: "#FFFBF5", color: "#78716C", borderLeft: "2px solid #FCD34D" }}
                  >
                    <i className="ri-lightbulb-line mr-0.5" style={{ fontSize: 9, color: "#F59E0B" }} />
                    {car.reason}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* 懒加载哨兵 */}
        {!isAllLoaded && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "#AAA" }}>
              <i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />
              <span>向下滑动加载更多...</span>
            </div>
          </div>
        )}

        {/* 全部加载完毕 */}
        {isAllLoaded && cars.length > INITIAL_SHOW_COUNT && (
          <div className="flex items-center justify-center py-2" style={{ borderTop: "1px solid #F0F2F8" }}>
            <span className="text-[10px]" style={{ color: "#C0C8D4" }}>
              — 已展示全部 {cars.length} 款车型 —
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
